// Dashboard › Atividade da equipe › Contatos detalhados (specs/19): cada contato da equipe, com o que aconteceu.
import { CONTATOS_EQUIPE_MAX, type CorStatus } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, apiBlob, mensagemErro } from '../api';
import { Carregando, Erro, Icon } from '../components/ui';
import { data, moeda, num } from '../format';
import { StatusBadge, useStatusClientes } from '../status';

type Periodo = 'hoje' | 'ontem' | '7d';
type Canal = 'WHATSAPP' | 'EMAIL' | 'LIGACAO' | 'PRESENCIAL';
interface Contato {
  id: string; createdAt: string; dia: string; hora: string; canal: Canal; mensagem: string | null; observacao: string | null;
  lote: boolean; campanha: string | null; usuarioId: string; vendedor: string;
  clienteId: string; cliente: string; empresa: string | null; statusAtual: string; statusAtualCor: CorStatus;
  statusDe: string | null; statusPara: string | null; vendaValor: string | null; vendaData: string | null;
  retornoEm: string | null; anteriorEm: string | null; anteriorCanal: Canal | null;
}
interface Resp { data: Contato[]; truncado: boolean; dia: string }

const CANAL: Record<Canal, { nome: string; icone: string; cls: string }> = {
  WHATSAPP: { nome: 'WhatsApp', icone: 'whatsapp', cls: 'wa' },
  EMAIL: { nome: 'E-mail', icone: 'email', cls: 'mail' },
  LIGACAO: { nome: 'Ligação', icone: 'telefone', cls: 'tel' },
  PRESENCIAL: { nome: 'Visita', icone: 'pin', cls: 'visita' },
};
const iniciais = (s: string) => s.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
const quando = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
function relativoDias(iso: string, ref: string) {
  const d = Math.floor((new Date(ref).getTime() - new Date(iso).getTime()) / 86_400_000);
  return d <= 0 ? 'no mesmo dia' : d === 1 ? 'há 1 dia' : `há ${d} dias`;
}

export function ContatosEquipe({ periodo, vendedores, vendedorInicial }: {
  periodo: Periodo; vendedores: Array<{ id: string; nome: string }>; vendedorInicial?: string | null;
}) {
  const statusLista = useStatusClientes();
  const [usuarioId, setUsuarioId] = useState(vendedorInicial ?? '');
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const [busca, setBusca] = useState('');
  const [canal, setCanal] = useState<Canal | ''>('');
  const [soVenda, setSoVenda] = useState(false);
  const [soStatus, setSoStatus] = useState(false);
  const [semLote, setSemLote] = useState(false);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [baixando, setBaixando] = useState(false);
  const [erroCsv, setErroCsv] = useState<string | null>(null);

  useEffect(() => { setUsuarioId(vendedorInicial ?? ''); }, [vendedorInicial]);
  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaDigitada.trim()), 300);
    return () => clearTimeout(t);
  }, [buscaDigitada]);

  const params = new URLSearchParams({ periodo });
  if (usuarioId) params.set('usuarioId', usuarioId);
  if (canal) params.set('canal', canal);
  if (busca.length >= 2) params.set('busca', busca);
  if (soVenda) params.set('soVenda', 'true');
  if (soStatus) params.set('soStatus', 'true');
  if (semLote) params.set('semLote', 'true');
  const qs = params.toString();
  const q = useQuery({
    queryKey: ['equipe-contatos', qs],
    queryFn: () => api<Resp>(`/dashboard/equipe/contatos?${qs}`),
    placeholderData: (p) => p,
    refetchInterval: periodo === 'hoje' ? 60_000 : false,
  });
  const linhas = q.data?.data ?? [];

  // cor dos status “de → para” (o histórico guarda o nome)
  const corDe = (nome: string | null) => statusLista.data?.find((s) => s.nome === nome)?.cor ?? 'cinza';
  const cont = (c: Canal) => linhas.filter((l) => l.canal === c).length;
  const vendas = linhas.filter((l) => l.vendaValor !== null);
  const somaVendas = vendas.reduce((s, l) => s + Number(l.vendaValor), 0);
  const todosAbertos = linhas.length > 0 && linhas.every((l) => abertos.has(l.id));
  const alternar = (id: string) => setAbertos((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function exportar() {
    setErroCsv(null);
    setBaixando(true);
    try {
      const blob = await apiBlob(`/dashboard/equipe/contatos?${qs}&formato=csv`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contatos-equipe-${periodo}-${q.data?.dia ?? 'hoje'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErroCsv(mensagemErro(e));
    } finally {
      setBaixando(false);
    }
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      <section className="card filtros-ce" aria-label="Filtros">
        <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
          <label className="field" style={{ minWidth: 200 }}><span>Vendedor</span>
            <select className="select" value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)}>
              <option value="">Todos os vendedores</option>
              {vendedores.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: '1 1 220px' }}><span>Cliente</span>
            <input className="input" placeholder="Nome ou empresa" value={buscaDigitada} onChange={(e) => setBuscaDigitada(e.target.value)} />
          </label>
          <div className="field"><span id="l-canal-ce">Canal</span>
            <div className="row" style={{ gap: 6 }} role="group" aria-labelledby="l-canal-ce">
              {([['', 'Todos'], ['WHATSAPP', 'WhatsApp'], ['EMAIL', 'E-mail'], ['LIGACAO', 'Ligação'], ['PRESENCIAL', 'Visita']] as const).map(([v, l]) => (
                <button key={v} type="button" className={`pill${canal === v ? ' on' : ''}`} aria-pressed={canal === v} onClick={() => setCanal(v)}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 18, alignItems: 'center' }}>
          <label className="check"><input type="checkbox" checked={soVenda} onChange={(e) => setSoVenda(e.target.checked)} />Só com venda</label>
          <label className="check"><input type="checkbox" checked={soStatus} onChange={(e) => setSoStatus(e.target.checked)} />Só com mudança de status</label>
          <label className="check"><input type="checkbox" checked={semLote} onChange={(e) => setSemLote(e.target.checked)} />Sem envios em lote de campanha</label>
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} disabled={baixando || !linhas.length} onClick={() => void exportar()}>
            <Icon name="download" size={14} />{baixando ? 'Gerando…' : 'Exportar CSV'}
          </button>
        </div>
        <Erro erro={erroCsv} />
      </section>

      <div className="grid-kpi" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div className="card kpi"><span className="label">Contatos</span><span className="value num kpi-ce">{num(linhas.length)}</span></div>
        <div className="card kpi"><span className="label">WhatsApp</span><span className="value num kpi-ce">{num(cont('WHATSAPP'))}</span></div>
        <div className="card kpi"><span className="label">E-mail</span><span className="value num kpi-ce">{num(cont('EMAIL'))}</span></div>
        <div className="card kpi"><span className="label">Ligação · Visita</span><span className="value num kpi-ce">{cont('LIGACAO')} · {cont('PRESENCIAL')}</span></div>
        <div className="card kpi"><span className="label">Mudanças de status</span><span className="value num kpi-ce">{num(linhas.filter((l) => l.statusPara).length)}</span></div>
        <div className="card kpi"><span className="label">Vendas</span>
          <span className="value num kpi-ce" style={vendas.length ? { color: 'var(--green-text)' } : undefined}>{vendas.length ? `${vendas.length} · ${moeda(somaVendas)}` : '0'}</span>
        </div>
      </div>

      <section className="card flush" aria-labelledby="h-contatos-ce">
        <div className="card-head" style={{ padding: '16px 18px 0' }}>
          <h2 id="h-contatos-ce">Contatos</h2>
          <span className="small faint">{num(linhas.length)} {linhas.length === 1 ? 'contato' : 'contatos'} · mais recentes primeiro</span>
          {linhas.length > 0 && (
            <button type="button" className="btn sm" style={{ marginLeft: 'auto' }}
              onClick={() => setAbertos(todosAbertos ? new Set() : new Set(linhas.map((l) => l.id)))}>{todosAbertos ? 'Recolher todos' : 'Expandir todos'}</button>
          )}
        </div>
        {q.data?.truncado && (
          <div className="alert warn" style={{ margin: '12px 18px 0' }}><Icon name="alerta" />
            <span>Mostrando os {num(CONTATOS_EQUIPE_MAX)} mais recentes. Use os filtros para ver o restante.</span></div>
        )}
        {q.isLoading ? <Carregando /> : q.error ? <Erro erro={mensagemErro(q.error)} /> : !linhas.length ? (
          <div className="empty">Nenhum contato com esses filtros no período.</div>
        ) : (
          <div className="table-wrap">
            <table className="tabela-ce">
              <thead><tr><th>Hora</th><th>Vendedor</th><th>Cliente</th><th>Status atual</th><th>Canal</th><th>O que aconteceu</th><th><span className="sr-only">Detalhes</span></th></tr></thead>
              <tbody>
                {linhas.map((l) => {
                  const aberta = abertos.has(l.id);
                  const c = CANAL[l.canal];
                  const resumo = l.observacao || (l.mensagem ? `“${l.mensagem.replace(/\s+/g, ' ')}”` : '');
                  return (
                    <FragmentoLinha key={l.id}>
                      <tr className={aberta ? 'aberta' : undefined}>
                        <td className="num small muted">{periodo === '7d' ? `${l.dia.slice(0, 5)} ` : ''}{l.hora}</td>
                        <td><span className="row" style={{ gap: 8, flexWrap: 'nowrap' }}><span className="av sm">{iniciais(l.vendedor)}</span>{l.vendedor}</span></td>
                        <td><Link to={`/clientes/${l.clienteId}`} style={{ fontWeight: 500 }}>{l.cliente}</Link>{l.empresa && <><br /><span className="small faint">{l.empresa}</span></>}</td>
                        <td><StatusBadge nome={l.statusAtual} cor={l.statusAtualCor} /></td>
                        <td><span className={`canal-ce ${c.cls}`}><span className="ic"><Icon name={c.icone} size={13} /></span>{c.nome}</span></td>
                        <td>
                          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                            {l.statusPara && (
                              <span className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
                                <StatusBadge nome={l.statusDe ?? '—'} cor={corDe(l.statusDe)} /><span className="faint" aria-label="para">→</span>
                                <StatusBadge nome={l.statusPara} cor={corDe(l.statusPara)} />
                              </span>
                            )}
                            {l.vendaValor !== null && <span className="badge green">Venda {moeda(l.vendaValor)}</span>}
                            {l.campanha && <span className="badge">Campanha {l.campanha}</span>}
                            {l.retornoEm && <span className="badge amber">Retorno {quando(l.retornoEm)}</span>}
                          </div>
                          {resumo && <div className="small muted resumo-ce">{resumo}</div>}
                        </td>
                        <td>
                          <button type="button" className="btn sm icon" aria-expanded={aberta} aria-label={`${aberta ? 'Fechar' : 'Ver'} detalhes de ${l.cliente}`} onClick={() => alternar(l.id)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={aberta ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} /></svg>
                          </button>
                        </td>
                      </tr>
                      {aberta && (
                        <tr className="detalhe-ce">
                          <td />
                          <td colSpan={6}>
                            <div className="detalhe-grid">
                              <div className="col" style={{ gap: 8, minWidth: 0 }}>
                                {l.mensagem && (
                                  <>
                                    <span className="rotulo">{l.canal === 'EMAIL' ? 'E-mail enviado · assunto' : 'Mensagem'}</span>
                                    <div className="bubble" style={{ whiteSpace: 'pre-line', maxWidth: 640 }}>{l.mensagem}</div>
                                  </>
                                )}
                                {l.observacao && (
                                  <>
                                    <span className="rotulo">Observação do vendedor</span>
                                    <div className="obs-ce">{l.observacao}</div>
                                  </>
                                )}
                                {!l.mensagem && !l.observacao && <span className="small faint">O vendedor não escreveu observação neste contato.</span>}
                              </div>
                              <div className="col" style={{ gap: 6 }}>
                                <span className="rotulo">Registro</span>
                                <span className="small"><span className="faint">Quando</span> {l.dia} às {l.hora} · {c.nome} · por {l.vendedor}</span>
                                {l.statusPara && <span className="small"><span className="faint">Status</span> {l.statusDe} → {l.statusPara}</span>}
                                {l.vendaValor !== null && (
                                  <span className="small"><span className="faint">Venda</span> <strong className="num" style={{ color: 'var(--green-text)', fontWeight: 500 }}>{moeda(l.vendaValor)}</strong>{l.vendaData ? ` · data da venda ${data(l.vendaData)}` : ''}</span>
                                )}
                                {l.retornoEm && <span className="small"><span className="faint">Retorno agendado</span> {quando(l.retornoEm)}</span>}
                                <span className="small"><span className="faint">Contato anterior</span> {l.anteriorEm ? `${relativoDias(l.anteriorEm, l.createdAt)} (${CANAL[l.anteriorCanal!]?.nome ?? '—'})` : 'nenhum, foi o primeiro'}</span>
                                <div className="row" style={{ gap: 8, marginTop: 6 }}>
                                  <Link className="btn sm" to={`/clientes/${l.clienteId}`}>Abrir ficha</Link>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </FragmentoLinha>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function FragmentoLinha({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
